import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Row, Col, Breadcrumb, Descriptions, Tag, Typography, Button,
    Space, Spin, message, Tabs, Table, Tooltip, Collapse
} from "antd";
import {
    HomeOutlined, ArrowLeftOutlined, ThunderboltOutlined,
    PrinterOutlined, FileExcelOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { exportRowsToExcel, fmtMoney } from "../../../utils/reportExport";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const HARVESTB_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Completed": "green",
};

const formatLKR = (value) =>
    `LKR ${parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatKg = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const HarvestBatchDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [collections, setCollections] = useState([]);
    const [wastages, setWastages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("collection");

    // Print / Excel export state
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [printTab, setPrintTab] = useState("collection");

    const fetchDetails = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/harvesting/full_detail.php?id=${id}`);
            if (res.data.status === "success") {
                setBatch(res.data.batch);
                setCollections(res.data.collections || []);
                setWastages(res.data.wastages || []);
            }
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to load harvest batch details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    // ── Financial calculations ────────────────────────────────────────────
    const costingRate = batch ? parseFloat(batch.costing_rate || 0) : 0;
    const sellingRate = batch ? parseFloat(batch.selling_rate || 0) : 0;
    const targetKg = batch ? parseFloat(batch.target_kg || 0) : 0;
    const totalTargetCost = costingRate * targetKg;
    const estimatedRevenue = sellingRate * targetKg;
    const estimatedProfit = estimatedRevenue - totalTargetCost;

    const collectionPercent = batch ? (() => {
        const target = parseFloat(batch.target_kg || 0);
        const collected = parseFloat(batch.total_collect_kg || 0);
        return target > 0 ? Math.min(100, Math.round((collected / target) * 100)) : 0;
    })() : 0;

    // ── Print & Excel export ──────────────────────────────────────────────

    const buildPrintDoc = (tab) => {
        if (tab === "collection") {
            return buildReportPDF({
                title: "HARVEST COLLECTION REPORT",
                subtitle: `Harvest Batch ${batch.harvestb_ref}`,
                columns: [
                    { header: "Collected Date" }, { header: "Field" }, { header: "Plant" },
                    { header: "Quantity", align: "right" }, { header: "Weight (Kg)", align: "right" },
                    { header: "Commercial Cost", align: "right" }, { header: "Expected Income", align: "right" },
                    { header: "Collected By" },
                ],
                rows: collections.map((r) => [
                    r.collect_date ? dayjs(r.collect_date).format("YYYY-MM-DD HH:mm") : "-",
                    r.field_code || "-",
                    r.plant_code || "-",
                    String(r.total_qty ?? 0),
                    fmtMoney(r.total_kg),
                    fmtMoney(r.commercial_cost),
                    fmtMoney(r.expect_income),
                    r.collect_by_name || "-",
                ]),
                summary: [
                    { label: "Batch Ref", value: batch.harvestb_ref },
                    { label: "Land / Section", value: `${batch.land_name || "-"} / ${batch.section_name || "-"}` },
                    { label: "Collected (Kg)", value: `${fmtMoney(batch.total_collect_kg)} / ${fmtMoney(batch.target_kg)} kg` },
                ],
                totalRow: [
                    "TOTAL", "", "",
                    String(collections.reduce((s, r) => s + Number(r.total_qty || 0), 0)),
                    fmtMoney(collections.reduce((s, r) => s + Number(r.total_kg || 0), 0)),
                    fmtMoney(collections.reduce((s, r) => s + Number(r.commercial_cost || 0), 0)),
                    fmtMoney(collections.reduce((s, r) => s + Number(r.expect_income || 0), 0)),
                    "",
                ],
                columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
            });
        }
        return buildReportPDF({
            title: "WASTAGE HARVEST REPORT",
            subtitle: `Harvest Batch ${batch.harvestb_ref}`,
            columns: [
                { header: "Recorded Date" }, { header: "Quantity", align: "right" },
                { header: "Weight (Kg)", align: "right" }, { header: "Commercial Lost", align: "right" },
                { header: "Reason" }, { header: "Recorded By" },
            ],
            rows: wastages.map((r) => [
                r.created_dt ? dayjs(r.created_dt).format("YYYY-MM-DD HH:mm") : "-",
                String(r.reject_qty ?? 0),
                fmtMoney(r.reject_kg),
                fmtMoney(r.commercial_lost),
                r.reject_reason || "-",
                r.created_by_name || "-",
            ]),
            summary: [
                { label: "Batch Ref", value: batch.harvestb_ref },
                { label: "Land / Section", value: `${batch.land_name || "-"} / ${batch.section_name || "-"}` },
                { label: "Wastage (Kg)", value: fmtMoney(batch.quality_reject_kg) },
            ],
            totalRow: [
                "TOTAL",
                String(wastages.reduce((s, r) => s + Number(r.reject_qty || 0), 0)),
                fmtMoney(wastages.reduce((s, r) => s + Number(r.reject_kg || 0), 0)),
                fmtMoney(wastages.reduce((s, r) => s + Number(r.commercial_lost || 0), 0)),
                "", "",
            ],
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
    };

    const handlePrint = async (tab) => {
        if (!batch) return;
        const rows = tab === "collection" ? collections : wastages;
        if (rows.length === 0) {
            message.warning("No data to print.");
            return;
        }
        setPrinting(true);
        try {
            setPrintTab(tab);
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildPrintDoc(tab);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
            setPrinting(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await buildPrintDoc(printTab);
            const prefix = printTab === "collection" ? "harvest-collection" : "wastage-harvest";
            doc.save(`${prefix}-${batch.harvestb_ref}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = async (tab) => {
        if (!batch) return;
        const rows = tab === "collection" ? collections : wastages;
        if (rows.length === 0) {
            message.warning("No data to export.");
            return;
        }
        setExporting(true);
        try {
            if (tab === "collection") {
                exportRowsToExcel({
                    filename: `harvest-collection-${batch.harvestb_ref}`,
                    sheetName: "Harvest Collection",
                    columns: [
                        { title: "Collected Date", dataIndex: "collect_date", width: 18 },
                        { title: "Field", dataIndex: "field_code", width: 14 },
                        { title: "Plant", dataIndex: "plant_code", width: 14 },
                        { title: "Quantity", dataIndex: "total_qty", width: 10 },
                        { title: "Weight (Kg)", dataIndex: "total_kg", width: 12 },
                        { title: "Commercial Cost", dataIndex: "commercial_cost", width: 16 },
                        { title: "Expected Income", dataIndex: "expect_income", width: 16 },
                        { title: "Collected By", dataIndex: "collect_by_name", width: 18 },
                    ],
                    rows: rows.map((r) => ({
                        collect_date: r.collect_date ? dayjs(r.collect_date).format("YYYY-MM-DD HH:mm") : "-",
                        field_code: r.field_code || "-",
                        plant_code: r.plant_code || "-",
                        total_qty: Number(r.total_qty || 0),
                        total_kg: fmtMoney(r.total_kg),
                        commercial_cost: fmtMoney(r.commercial_cost),
                        expect_income: fmtMoney(r.expect_income),
                        collect_by_name: r.collect_by_name || "-",
                    })),
                    extraRows: [[
                        "TOTAL", "", "",
                        rows.reduce((s, r) => s + Number(r.total_qty || 0), 0),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.total_kg || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_cost || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.expect_income || 0), 0)),
                        "",
                    ]],
                });
            } else {
                exportRowsToExcel({
                    filename: `wastage-harvest-${batch.harvestb_ref}`,
                    sheetName: "Wastage Harvest",
                    columns: [
                        { title: "Recorded Date", dataIndex: "created_dt", width: 18 },
                        { title: "Quantity", dataIndex: "reject_qty", width: 10 },
                        { title: "Weight (Kg)", dataIndex: "reject_kg", width: 12 },
                        { title: "Commercial Lost", dataIndex: "commercial_lost", width: 16 },
                        { title: "Reason", dataIndex: "reject_reason", width: 30 },
                        { title: "Recorded By", dataIndex: "created_by_name", width: 18 },
                    ],
                    rows: rows.map((r) => ({
                        created_dt: r.created_dt ? dayjs(r.created_dt).format("YYYY-MM-DD HH:mm") : "-",
                        reject_qty: Number(r.reject_qty || 0),
                        reject_kg: fmtMoney(r.reject_kg),
                        commercial_lost: fmtMoney(r.commercial_lost),
                        reject_reason: r.reject_reason || "-",
                        created_by_name: r.created_by_name || "-",
                    })),
                    extraRows: [[
                        "TOTAL",
                        rows.reduce((s, r) => s + Number(r.reject_qty || 0), 0),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.reject_kg || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_lost || 0), 0)),
                        "", "",
                    ]],
                });
            }
            message.success("Excel exported successfully.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    // ── Tables ────────────────────────────────────────────────────────────

    const recordColumns = [
        {
            title: "Collected Date",
            dataIndex: "collect_date",
            key: "collect_date",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Field", dataIndex: "field_code", key: "field_code", render: (val) => val || "-" },
        { title: "Plant", dataIndex: "plant_code", key: "plant_code", render: (val) => val || "-" },
        { title: "Quantity", dataIndex: "total_qty", key: "total_qty", align: "right", render: (val) => Number(val || 0).toLocaleString() },
        { title: "Weight (Kg)", dataIndex: "total_kg", key: "total_kg", align: "right", render: (val) => formatKg(val) },
        { title: "Commercial Cost", dataIndex: "commercial_cost", key: "commercial_cost", align: "right", render: (val) => formatLKR(val) },
        { title: "Expected Income", dataIndex: "expect_income", key: "expect_income", align: "right", render: (val) => formatLKR(val) },
        { title: "Collected By", dataIndex: "collect_by_name", key: "collect_by_name", render: (val) => val || "-" },
    ];

    const wastageColumns = [
        {
            title: "Recorded Date",
            dataIndex: "created_dt",
            key: "created_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Quantity", dataIndex: "reject_qty", key: "reject_qty", align: "right", render: (val) => Number(val || 0).toLocaleString() },
        { title: "Weight (Kg)", dataIndex: "reject_kg", key: "reject_kg", align: "right", render: (val) => formatKg(val) },
        { title: "Commercial Lost", dataIndex: "commercial_lost", key: "commercial_lost", align: "right", render: (val) => formatLKR(val) },
        { title: "Reason", dataIndex: "reject_reason", key: "reject_reason", ellipsis: true, width: 240, render: (val) => val || "-" },
        { title: "Recorded By", dataIndex: "created_by_name", key: "created_by_name", render: (val) => val || "-" },
    ];

    const renderExportBar = (tab) => (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, margin: "0 16px 12px" }}>
            <Tooltip title="Print (respects the loaded records)">
                <Button icon={<PrinterOutlined />} onClick={() => handlePrint(tab)} loading={printing}>
                    Print
                </Button>
            </Tooltip>
            <Tooltip title="Export to Excel">
                <Button
                    icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                    onClick={() => handleExportExcel(tab)}
                    loading={exporting}
                >
                    Excel
                </Button>
            </Tooltip>
        </div>
    );

    const tabItems = [
        {
            key: "collection",
            label: `Harvest Collection (${collections.length})`,
            children: (
                <>
                    {renderExportBar("collection")}
                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={recordColumns}
                            dataSource={collections}
                            rowKey="harvestcol_id"
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                        />
                    </div>
                </>
            ),
        },
        {
            key: "wastage",
            label: `Wastage Harvest (${wastages.length})`,
            children: (
                <>
                    {renderExportBar("wastage")}
                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={wastageColumns}
                            dataSource={wastages}
                            rowKey="harvestwas_id"
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                        />
                    </div>
                </>
            ),
        },
    ];

    // Collapsible batch detail sections
    const collapseItems = batch ? [
        {
            key: "info",
            label: (
                <Space>
                    <Typography.Text strong>Batch Information</Typography.Text>
                    <Tag color={HARVESTB_STATUS_COLORS[batch.harvestb_status] || "default"} style={{ fontWeight: 600 }}>
                        {batch.harvestb_status || "-"}
                    </Tag>
                </Space>
            ),
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Batch Ref" span={2}>
                        <Typography.Text strong>{batch.harvestb_ref}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Land">{batch.land_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Section">{batch.section_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Harvest Season">{batch.harvest_season || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={HARVESTB_STATUS_COLORS[batch.harvestb_status] || "default"} style={{ fontWeight: 600 }}>
                            {batch.harvestb_status || "-"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Target Quantity">{Number(batch.target_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Target Weight (Kg)">{Number(batch.target_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Target Start Date">
                        {batch.harvest_start_date ? dayjs(batch.harvest_start_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Target End Date">
                        {batch.harvest_end_date ? dayjs(batch.harvest_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Costing Rate (per Kg)">{formatLKR(batch.costing_rate)}</Descriptions.Item>
                    <Descriptions.Item label="Selling Rate (per Kg)">{formatLKR(batch.selling_rate)}</Descriptions.Item>
                    <Descriptions.Item label="Harvest Closing Date">
                        {batch.harvest_closing_date ? dayjs(batch.harvest_closing_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Batch Id">{batch.harvestb_id}</Descriptions.Item>
                    <Descriptions.Item label="Created By">{batch.creator_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Created Date">
                        {batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated By">{batch.checker_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated Date">
                        {batch.ch_dt ? dayjs(batch.ch_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
        {
            key: "summary",
            label: <Typography.Text strong>Collection &amp; Financial Summary</Typography.Text>,
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Total Collected Quantity">{Number(batch.total_collect_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Total Collected Weight (Kg)">{Number(batch.total_collect_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Quality Reject Quantity">{Number(batch.quality_reject_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Quality Reject Weight (Kg)">{Number(batch.quality_reject_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Total Target Cost">
                        <Typography.Text strong>{formatLKR(totalTargetCost)}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {costingRate.toFixed(2)} × {Number(targetKg).toLocaleString()} kg
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estimated Revenue">
                        <Typography.Text strong>{formatLKR(estimatedRevenue)}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {sellingRate.toFixed(2)} × {Number(targetKg).toLocaleString()} kg
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estimated Profit" span={2}>
                        <Typography.Text
                            strong
                            style={{ fontSize: 16, color: estimatedProfit >= 0 ? "#3f8600" : "#cf1322" }}
                        >
                            {formatLKR(estimatedProfit)}
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Target Completion" span={2}>
                        <Typography.Text>{collectionPercent}% of target weight collected</Typography.Text>
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
    ] : [];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Harvesting' },
                                { title: 'Harvest Batches' },
                                { title: batch?.harvestb_ref || 'Details' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ThunderboltOutlined /> Harvest Batch Details
                        </h1>
                        <p className="fluid-page-subtitle">
                            {batch ? `Complete details for harvest batch ${batch.harvestb_ref}` : "Loading harvest batch details..."}
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/harvesting/harvest-batches')}
                            className="fluid-btn-secondary"
                        >
                            Back to Batches
                        </Button>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Spin spinning={loading}>
                    {batch && (
                        <>
                            <div className="fluid-card" style={{ marginBottom: 16 }}>
                                <Collapse
                                    items={collapseItems}
                                    defaultActiveKey={["info", "summary"]}
                                    expandIconPosition="end"
                                />
                            </div>

                            <div className="fluid-card">
                                <div style={{ padding: "16px 0 0" }}>
                                    <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                                </div>
                            </div>
                        </>
                    )}
                </Spin>
            </Content>

            <Footerdiv />

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title={printTab === "collection" ? "Harvest Collection Report" : "Wastage Harvest Report"}
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                showFiltersOption={false}
                onDownload={handleDownloadPDF}
            />
        </Layout>
    );
};

export default HarvestBatchDetail;
